/**
 * callMenuNaming.js — 일일콜 관리 메뉴 이름 (2026-09-24, 사용자 확정)
 *
 * 사용자 원문: "지금 현재 일일콜현황 관리 를 일일콜현황 관리(통합)으로 바꾸고 일일콜현황 관리(파인그룹)을
 *   일일콜현황 관리로 바꿀까? 왜냐하면 dot나 lnj처럼 회사 고르면 나오는 일일콜현황 관리는 그 회사꺼라는 걸 아니깐"
 *   + 회사가 하나뿐인 테넌트(미팅링크·유기자차)는 메뉴 하나만.
 *
 *   A = /admin/tm-calls       (TM_CALL_MANAGEMENT)       같은 테넌트 회사 콜 전부
 *   B = /admin/tm-calls-jeju  (TM_CALL_MANAGEMENT_JEJU)  본사 회사 콜 (옛 '(제주)')
 *
 *   일일콜 켠 회사 2곳 이상 → A '일일콜현황 관리(통합)' / B '일일콜현황 관리'
 *   1곳                    → A '일일콜현황 관리' 하나만 (A 가 있으면 B 는 뺀다 — 같은 콜을 보여 준다)
 *   모름(첫 화면·실패)      → 이름을 바꾸지 않는다(menuConfig 기본 이름) — 아무도 메뉴를 잃지 않는다
 *
 * 규칙: **메뉴를 새로 만들지 않고 이름을 바꾸거나 빼기만 한다**(권한 없는 메뉴가 생길 수 없다, fail-closed).
 *   같은 이름 두 개가 한 사이드바에 서지 않게 마지막에 한 번 더 본다.
 *
 * 2026-10-14 (통합 계획 3단계 7번): 회사 2곳 이상 테넌트의 A '(통합)' 칸은 없앴다 — 같은 내용은 작업 대상 → 통합관리의
 *   통합 일일콜 메뉴로 옮겼다. A 는 /api/me callCompanyCount 가 **정확히 1**일 때만 남는다(isCallAllVisible,
 *   menuComposition 이 뺀다). 옛 A 주소로 들어오면 callAllRouteDecision 이 보낼 곳을 정한다(AppRoutes.company.jsx CallAllRoute).
 */
export const CALL_ALL_PATH = '/admin/tm-calls';
export const CALL_HQ_PATH = '/admin/tm-calls-jeju';
const BASE = '일일콜현황 관리';

/** A '(통합)' 칸을 보이나 — 자기 테넌트 일일콜 켠 회사가 정확히 1곳일 때만. 모르면(null·실패) 숨긴다(fail-closed). */
export function isCallAllVisible(me) {
  return Number(me?.callCompanyCount) === 1;
}

/**
 * 옛 A 주소(/admin/tm-calls)로 들어왔을 때 (사용자 결정 4-a, 2026-10-13).
 *   A 를 쓰는 테넌트면 그대로 그린다. 아니면 본사 화면(B)을 열 수 있으면 B → 그 사람의 통합 일일콜 메뉴(첫 번째)
 *   → 첫 화면('/'). 첫 화면 규칙(usePermissions)도 A 를 빼므로 두 곳을 오가지 않는다.
 * @returns {{wait:true}|{render:true}|{to:string}}
 */
export function callAllRouteDecision({ meReady, callAllVisible, canOpenHq, integratedReady, integratedMenus }) {
  if (!meReady) return { wait: true };
  if (callAllVisible) return { render: true };
  if (canOpenHq) return { to: CALL_HQ_PATH };
  if (!integratedReady) return { wait: true };
  const tm = (Array.isArray(integratedMenus) ? integratedMenus : []).find((m) => m?.feature_type === 'tm_call' && m?.id != null);
  if (tm) return { to: `/admin/integrated/${tm.id}` };
  return { to: '/' };
}

/** 'single' | 'multi' | 'unknown' */
export function callNamingState(info) {
  if (!info || info.status !== 'known') return 'unknown';
  const n = Number(info.companyCount);
  if (n === 1) return 'single';
  if (n >= 2) return 'multi';
  return 'unknown';
}

/** 화면 제목·부제목(CallRegister). null = 바꾸지 않음. */
export function callPageHeader(kind, info) {
  const state = callNamingState(info);
  const name = info?.primaryName || '';
  if (kind === 'all') {
    if (state !== 'multi') return null;
    return { title: `${BASE}(통합)`, subtitle: '모든 회사의 일일 콜 기록을 한곳에서 관리합니다.' };
  }
  if (kind === 'hq') {
    return { title: BASE, subtitle: `${state !== 'unknown' && name ? name : '본사'} 일일 콜 기록을 관리합니다.` };
  }
  return null;
}

/** 사이드바·홈 카드 그룹의 A/B 이름을 규칙대로. 입력에 없던 항목은 절대 더하지 않는다. */
export function nameCallMenus(groups, info) {
  const state = callNamingState(info);
  if (state === 'unknown' || !Array.isArray(groups)) return groups;
  const all = groups.flatMap((g) => g.items || []);
  const hasA = all.some((it) => it.to === CALL_ALL_PATH);
  const hasB = all.some((it) => it.to === CALL_HQ_PATH);
  if (!hasA && !hasB) return groups;

  const dropB = state === 'single' && hasA;
  const name = info.primaryName || '';
  let out = groups
    .map((g) => ({
      ...g,
      items: (g.items || [])
        .filter((it) => !(dropB && it.to === CALL_HQ_PATH))
        .map((it) => {
          if (it.to === CALL_ALL_PATH) {
            return state === 'multi'
              ? { ...it, label: `${BASE}(통합)`, desc: '모든 회사의 일일 콜 기록을 한곳에서 관리합니다.' }
              : { ...it, label: BASE };
          }
          if (it.to === CALL_HQ_PATH) {
            return { ...it, label: BASE, desc: name ? `${name} 일일 콜 기록을 관리합니다.` : it.desc };
          }
          return it;
        }),
    }))
    .filter((g) => g.items.length > 0);

  // 같은 이름 두 개 금지: B 가 다른 항목(회사별 '일일콜현황 관리' 등)과 이름이 같으면 회사 이름을 붙인다
  const others = new Set(out.flatMap((g) => g.items).filter((it) => it.to !== CALL_HQ_PATH).map((it) => it.label));
  if (others.has(BASE)) {
    out = out.map((g) => ({
      ...g,
      items: g.items.map((it) => (it.to === CALL_HQ_PATH ? { ...it, label: `${BASE}(${name || '본사'})` } : it)),
    }));
  }
  return out;
}
